/** Client-side equity / realized-PnL samples for Cabinet monitoring charts.
 * Spot and Futures are separate series (WEEX wallets don't share a balance).
 */

export type EquityMarket = 'futures' | 'spot';

export type EquitySample = {
  t: number;
  equity: number;
  /** Unrealized PnL at sample time (kept for wallet ≈ equity − upnl). */
  upnl: number;
  /**
   * Realized PnL vs first sample’s wallet (equity − upnl).
   * Filled on load / push so older localStorage rows still work.
   */
  realized?: number;
};

const PREFIX = 'termix.equityHist.v3:';
const V2_PREFIX = 'termix.equityHist.v2:';
const LEGACY_PREFIX = 'termix.equityHist.v1:';
/** ~24h at 12s poll — enough for period filters in Cabinet monitoring. */
const MAX_POINTS = 7200;

const storageKey = (keyId: string, market: EquityMarket) =>
  `${PREFIX}${keyId || 'anon'}:${market}`;
const v2Key = (keyId: string) => `${V2_PREFIX}${keyId || 'anon'}`;
const legacyKey = (keyId: string) => `${LEGACY_PREFIX}${keyId || 'anon'}`;

const walletOf = (s: { equity: number; upnl: number }) => s.equity - (Number.isFinite(s.upnl) ? s.upnl : 0);

const withRealized = (rows: EquitySample[]): EquitySample[] => {
  if (!rows.length) return rows;
  const deposit = walletOf(rows[0]);
  return rows.map((r) => ({
    ...r,
    realized: walletOf(r) - deposit,
  }));
};

const parseRows = (raw: string | null): EquitySample[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const rows = parsed
      .map((p) => ({
        t: Number(p?.t) || 0,
        equity: Number(p?.equity),
        upnl: Number(p?.upnl) || 0,
      }))
      .filter((p) => p.t > 0 && Number.isFinite(p.equity));
    return withRealized(rows);
  } catch {
    return [];
  }
};

export const loadEquityHistory = (
  keyId: string,
  market: EquityMarket = 'futures',
): EquitySample[] => {
  if (!keyId || typeof localStorage === 'undefined') return [];
  const cur = parseRows(localStorage.getItem(storageKey(keyId, market)));
  if (cur.length) return cur;
  // One-shot: pre-split history lived under v2 (no market) — treat as futures only.
  if (market === 'futures') {
    const v2 = parseRows(localStorage.getItem(v2Key(keyId)));
    if (v2.length) {
      saveEquityHistory(keyId, 'futures', v2);
      try { localStorage.removeItem(v2Key(keyId)); } catch { /* ignore */ }
      return v2;
    }
    const legacy = parseRows(localStorage.getItem(legacyKey(keyId)));
    if (legacy.length) {
      saveEquityHistory(keyId, 'futures', legacy);
      try { localStorage.removeItem(legacyKey(keyId)); } catch { /* ignore */ }
      return legacy;
    }
  }
  return [];
};

export const saveEquityHistory = (
  keyId: string,
  market: EquityMarket,
  samples: EquitySample[],
) => {
  if (!keyId || typeof localStorage === 'undefined') return;
  try {
    const slim = withRealized(samples).slice(-MAX_POINTS).map((s) => ({
      t: s.t,
      equity: s.equity,
      upnl: s.upnl,
    }));
    localStorage.setItem(storageKey(keyId, market), JSON.stringify(slim));
  } catch { /* quota / private mode */ }
};

/** First sample wallet = “deposit” baseline for realized PnL. */
export const depositFromSamples = (samples: EquitySample[]): number | null => {
  if (!samples.length) return null;
  const d = walletOf(samples[0]);
  return Number.isFinite(d) ? d : null;
};

/** Append a sample; skip near-duplicates within minGapMs. */
export const pushEquitySample = (
  keyId: string,
  equity: number,
  upnl: number,
  opts?: { minGapMs?: number; now?: number; market?: EquityMarket },
): EquitySample[] => {
  const market = opts?.market || 'futures';
  if (!keyId || !Number.isFinite(equity)) return loadEquityHistory(keyId, market);
  const now = opts?.now ?? Date.now();
  const minGap = opts?.minGapMs ?? 8_000;
  const prev = loadEquityHistory(keyId, market);
  const point = { t: now, equity, upnl: Number.isFinite(upnl) ? upnl : 0 };
  const last = prev[prev.length - 1];
  if (last && now - last.t < minGap) {
    const next = withRealized([...prev.slice(0, -1), point]);
    saveEquityHistory(keyId, market, next);
    return next;
  }
  const next = withRealized([...prev, point]);
  saveEquityHistory(keyId, market, next);
  return next;
};
